import { GifsResult } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import { GifOverlayProps, Grid as SDKGrid } from '@giphy/react-components'
import { uniq, without } from 'lodash'
import React, {
    ElementType,
    MouseEvent,
    SyntheticEvent,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useHistory } from 'react-router'
import { useKeyPress } from 'react-use'
import BulkEditor from 'shared/components/bulk-editor'
import { useCanBulkEdit } from 'shared/hooks/use-permissions'
import { useSameObjectRef } from 'shared/hooks/use-same-ref'
import { grid12 } from 'shared/util/grid'
import isMobile from 'shared/util/is-mobile'
import { relativeUrl } from 'shared/util/url'
import styled from 'styled-components'
import BulkEditModeContext from './context/bulk-edit-mode'
import GAEventContext from './context/ga-events'
import GifContext from './context/gif'
import ModalContext from './context/modal'
import UserContext from './context/user'
import { PublicAPIError } from 'shared/api/errors'
import GridOverlay, { GridOverlayContext } from './grid-overlay'
import TV from './tv'
import { getGifWidthForTV, getTvProps, TVConfig, TVProps } from './tv-util'

const Container = styled.div`
    position: relative;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
`

type Props = {
    className?: string
    fetchGifs: (offset: number) => Promise<GifsResult>
    width?: number
    noRedux?: boolean
    standaloneSelectionMode?: boolean // for the gif add grid, need to think of a better name
    onSelectionUpdate?: (gifs: GifId[]) => void
    tvConfig?: TVConfig
    columns?: number
    onGifGAClick?: (gif: Gif) => void
    // this is just for GA but didn't want to name it with GA
    onAttributionClick?: (gif: Gif) => void
    gutter?: number
    Overlay?: ElementType<GifOverlayProps>
    initialGifs?: IGif[]
}
type GifId = string | number
type Gif = object

const defaultGutter = isMobile() ? 6 : 16
const Grid = ({
    fetchGifs,
    width = grid12,
    gutter = defaultGutter,
    columns = 3,
    noRedux,
    standaloneSelectionMode,
    onSelectionUpdate,
    className,
    onGifGAClick,
    onAttributionClick,
    tvConfig,
    Overlay,
    initialGifs,
}: Props) => {
    const { receivedGifs } = useContext(GifContext)
    const { openBulkEditPanel } = useContext(ModalContext)

    const { isBulkEditMode, setBulkEditGifIds: setSelectedGifs, bulkEditGifIds: selectedGifs } = useContext(
        BulkEditModeContext
    )
    const { user } = useContext(UserContext)
    const iSelectionMode = !!(isBulkEditMode || standaloneSelectionMode)
    const [gifs, setGifs] = useState<GifId[]>([])
    const history = useHistory()
    const { onGifClick: sendGAGifClick, onGifRightClick: sendGARightClick, onPagination } = useContext(GAEventContext)
    const onError = useCallback((e: Error) => {
        if (e instanceof PublicAPIError) {
            // TODO Add a better handler here.
        }
        console.error(`Grid fetch error: ${e?.message}`)
    }, [])
    const [isEscPressed] = useKeyPress((event: KeyboardEvent) => event.keyCode === 27)
    const page = useRef(1)
    const allowBulkEdit = useCanBulkEdit()
    const gifWidth = getGifWidthForTV(width, gutter, columns)
    let tvProps: TVProps | undefined
    let columnOffsets
    if (tvConfig) {
        tvProps = getTvProps(tvConfig, gifWidth, gutter, columns)
        columnOffsets = tvProps.columnOffsets
    }
    const fetchWrapped = async (offset: number) => {
        const result = await fetchGifs(offset)
        const { data: gifs = [] } = result
        setGifs(gifs.map(gif => gif.id))
        onPagination(page.current++)

        if (!noRedux) {
            receivedGifs({ gifs })
        }
        return result
    }

    const [lastSelected, setLastSelected] = useState<GifId | undefined>()

    useEffect(() => {
        setSelectedGifs([])
    }, [isEscPressed, setSelectedGifs])

    useEffect(() => {
        return () => setSelectedGifs([])
    }, [setSelectedGifs])

    useEffect(() => {
        onSelectionUpdate?.(selectedGifs)
    }, [selectedGifs, onSelectionUpdate])

    const onGifRightClick = useCallback((gif: IGif, e: SyntheticEvent<HTMLElement, Event>) => {
        sendGARightClick(gif, e)
        if (isMobile()) {
            e.preventDefault()
        }
    }, [])

    const onGifClick = useCallback(
        (gif: IGif, e: SyntheticEvent<HTMLElement, Event>) => {
            onGifGAClick ? onGifGAClick(gif) : sendGAGifClick(gif, e)
            e.preventDefault()
            const { url } = gif
            const mouseEvent = (e as unknown) as MouseEvent

            if (!iSelectionMode) {
                if (mouseEvent.metaKey) {
                    window.open(url, '_blank')
                } else {
                    // default gif click history pushes
                    if (noRedux && isMobile()) {
                        location.href = relativeUrl(gif.url)
                    } else {
                        history.push(relativeUrl(gif.url))
                    }

                    //
                }
            } else {
                // Selection mode stuff
                if (!standaloneSelectionMode) {
                    // standaloneSelectionMode means no bulk editor
                    openBulkEditPanel(allowBulkEdit)
                }
                const { id } = gif
                if (mouseEvent.metaKey) {
                    if (isBulkEditMode || standaloneSelectionMode) {
                        if (selectedGifs.indexOf(id) !== -1) {
                            setSelectedGifs(without(selectedGifs, id))
                        } else {
                            setSelectedGifs([...selectedGifs, id])
                        }
                    } else {
                        window.open(url, '_blank')
                    }
                } else if (mouseEvent.shiftKey) {
                    const startIndex = lastSelected ? gifs.indexOf(lastSelected) : 0
                    const endIndex = gifs.indexOf(gif.id)
                    setSelectedGifs(
                        // TODO I think this is right...
                        uniq([
                            ...selectedGifs,
                            ...gifs.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1),
                        ])
                    )
                } else {
                    setSelectedGifs([id])
                }
                setLastSelected(id)
            }
        },
        [
            // exhaustive deps suck
            allowBulkEdit,
            gifs,
            history,
            iSelectionMode,
            lastSelected,
            openBulkEditPanel,
            selectedGifs,
            sendGAGifClick,
            setSelectedGifs,
            standaloneSelectionMode,
        ]
    )

    const gridOverlayValue = useSameObjectRef({
        onGifClick,
        onAttributionClick,
        selectedGifs,
        iSelectionMode,
        Overlay,
    })
    return (
        <>
            <Container>
                {tvProps && <TV {...tvProps} />}
                <GridOverlayContext.Provider value={gridOverlayValue}>
                    <SDKGrid
                        initialGifs={initialGifs}
                        user={user}
                        onGifsFetchError={onError}
                        width={width}
                        gutter={gutter}
                        onGifClick={onGifClick}
                        onGifRightClick={onGifRightClick}
                        columns={columns}
                        fetchGifs={fetchWrapped}
                        overlay={GridOverlay}
                        className={className}
                        columnOffsets={columnOffsets}
                        hideAttribution
                    />
                </GridOverlayContext.Provider>
            </Container>
            {!standaloneSelectionMode && <BulkEditor />}
        </>
    )
}

export default Grid
